import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BellRing } from 'lucide-react';
import { keyBytes, pushSupport, subscriptionBody } from '../../../lib/push';
import { sharingApi } from '../api';

const SW_URL = '/sw.js';

function environment() {
  const nav = typeof navigator === 'undefined' ? undefined : navigator;
  const ios = !!nav && /iPad|iPhone|iPod/.test(nav.userAgent) && !('MSStream' in window);
  const standalone =
    (typeof matchMedia === 'function' && matchMedia('(display-mode: standalone)').matches) ||
    (nav as { standalone?: boolean } | undefined)?.standalone === true;
  return {
    serviceWorker: !!nav && 'serviceWorker' in nav,
    pushManager: typeof window !== 'undefined' && 'PushManager' in window,
    notification: typeof Notification !== 'undefined',
    ios,
    standalone,
  };
}

async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await navigator.serviceWorker.getRegistration(SW_URL);
  return (await reg?.pushManager.getSubscription()) ?? null;
}

/**
 * "Push on this device" in the notifications panel (U52, OIL-26): subscribes this browser to
 * web push (permission prompt, service worker); on iPhone / iPad it explains the home-screen app.
 * Hidden when the server has no VAPID keys.
 */
export function PushToggle() {
  const { t } = useTranslation();
  const [key, setKey] = useState<string | null>(null);
  const [on, setOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const support = pushSupport(environment());
  useEffect(() => {
    let alive = true;
    sharingApi()
      .getPushConfig()
      .then(
        (c) => alive && setKey(c?.publicKey ?? null),
        () => undefined,
      );
    if (support === 'ok')
      void currentSubscription().then(
        (s) => alive && setOn(s !== null),
        () => undefined,
      );
    return () => {
      alive = false;
    };
  }, [support]);
  if (key === null) return null;
  if (support !== 'ok')
    return <p className="muted small push-hint">{t(`sharing.push.${support}`)}</p>;

  const enable = async () => {
    setBusy(true);
    setError(null);
    try {
      if ((await Notification.requestPermission()) !== 'granted') throw new Error('denied');
      const reg = await navigator.serviceWorker.register(SW_URL);
      await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: keyBytes(key),
      });
      const body = subscriptionBody(sub.toJSON());
      if (!body) throw new Error('incomplete');
      await sharingApi().subscribePush(body);
      setOn(true);
    } catch (err) {
      setError(
        t((err as Error).message === 'denied' ? 'sharing.push.denied' : 'sharing.push.failed'),
      );
    } finally {
      setBusy(false);
    }
  };
  const disable = async () => {
    setBusy(true);
    try {
      const sub = await currentSubscription();
      if (sub) {
        await sharingApi().unsubscribePush(sub.endpoint);
        await sub.unsubscribe();
      }
      setOn(false);
    } catch {
      setError(t('sharing.push.failed'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="push-toggle">
      <label className="check">
        <input
          type="checkbox"
          checked={on}
          disabled={busy}
          onChange={(e) => void (e.target.checked ? enable() : disable())}
        />
        <BellRing size={13} aria-hidden="true" /> {t('sharing.push.label')}
      </label>
      {error && (
        <p className="field-error small" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
